import { StringId } from '@strings';
import {
  APP_ICON_ARROW_POINTER,
  APP_ICON_HAND,
  APP_ICON_PLUS,
  APP_ICON_REDO,
  APP_ICON_UNDO,
  APP_ICON_UP_DOWN_LEFT_RIGHT,
  type AppIcon
} from '@ui';

export enum CommandId {
  CANCEL = 'cancel',

  TOOL_ARRANGE = 'toolArrange',
  TOOL_CREATE = 'toolCreate',
  TOOL_PAN = 'toolPan',
  TOOL_SELECT = 'toolSelect',

  CREATE_UNIT = 'createUnit',
  DELETE_SELECTION = 'deleteSelection',
  GROUP_SELECTION = 'groupSelection',
  REVERSE_EDGES = 'reverseEdges',

  UNDO = 'undo',
  REDO = 'redo',

  SELECT_ALL = 'selectAll',
  SELECT_SUCCESSORS = 'selectSuccessors',
  SELECT_ALL_SUCCESSORS = 'selectAllSuccessors',
  SELECT_PREDECESSORS = 'selectPredecessors',
  SELECT_ALL_PREDECESSORS = 'selectAllPredecessors',
  SELECT_NEIGHBORS = 'selectNeighbors',
  SELECT_ALL_NEIGHBORS = 'selectAllNeighbors',

  COLLAPSE_GROUPS = 'collapseGroups',
  DEEP_COLLAPSE_GROUPS = 'deepCollapseGroups',
  EXPAND_GROUPS = 'expandGroups',
  DEEP_EXPAND_GROUPS = 'deepExpandGroups',

  ZOOM_100_PERCENT = 'zoom100Percent',
  ZOOM_200_PERCENT = 'zoom200Percent',
  ZOOM_TO_FIT = 'zoomToFit',

  CENTER_ON_POINT = 'centerOnPoint',
  FOCUS_SELECTION = 'focusSelection',

  NEXT_LOCALE = 'nextLocale',
  PREV_LOCALE = 'prevLocale',
  TOGGLE_COLOR_MODE = 'toggleColorMode',

  RELOAD_WINDOW = 'reloadWindow',
  TOGGLE_BOTTOM_BAR = 'toggleBottomBar',

  SHOW_EDITOR = 'showEditor',

  LAYOUT_LEFT_TO_RIGHT = 'layoutLeftToRight',
  LAYOUT_TOP_TO_BOTTOM = 'layoutTopToBottom',
  LAYOUT_RIGHT_TO_LEFT = 'layoutRightToLeft',
  LAYOUT_BOTTOM_TO_TOP = 'layoutBottomToTop',
  LAYOUT_UNCONSTRAINED = 'layoutUnconstrained',

  TOGGLE_BUNDLED_ROUTING = 'toggleBundledRouting',
  TOGGLE_ALIGN_ROOTS = 'toggleAlignRoots',
  TOGGLE_ALIGN_LEAVES = 'toggleAlignLeaves',

  SET_SELECTED_UNITS_CLASS = 'setSelectedUnitsClass',
  SET_LOCALE = 'setLocale'
}

export interface KeyTrigger {
  base: string;
  mac?: string;
  windows?: string;
  linux?: string;
}

export interface CommandSpec {
  id: CommandId;
  name: StringId;
  shortName?: StringId;
  description?: StringId;
  keyTriggers?: KeyTrigger;
  icon?: AppIcon;
}

export const commandSpecs: CommandSpec[] = [
  {
    id: CommandId.CANCEL,
    name: StringId.CANCEL_COMMAND,
    keyTriggers: { base: 'ESC' }
  },
  {
    id: CommandId.CREATE_UNIT,
    name: StringId.CREATE_UNIT_COMMAND,
    keyTriggers: { base: 'Control+E', mac: 'Command+E' }
  },
  {
    id: CommandId.DELETE_SELECTION,
    name: StringId.DELETE_SELECTION_COMMAND,
    keyTriggers: { base: 'Delete', mac: 'Backspace' }
  },
  {
    id: CommandId.GROUP_SELECTION,
    name: StringId.GROUP_SELECTION_COMMAND,
    keyTriggers: { base: 'Control+G', mac: 'Command+G' }
  },
  {
    id: CommandId.RELOAD_WINDOW,
    name: StringId.RELOAD_WINDOW_COMMAND,
    keyTriggers: { base: 'Control+Shift+R', mac: 'Command+Shift+R' }
  },
  {
    id: CommandId.REVERSE_EDGES,
    name: StringId.REVERSE_EDGES_COMMAND,
    keyTriggers: { base: 'Control+R', mac: 'Command+R' }
  },

  {
    id: CommandId.SELECT_ALL,
    name: StringId.SELECT_ALL_COMMAND,
    keyTriggers: { base: 'Control+A', mac: 'Command+A' }
  },
  {
    id: CommandId.SELECT_SUCCESSORS,
    name: StringId.SELECT_SUCCESSORS_COMMAND,
    keyTriggers: { base: ']' }
  },
  {
    id: CommandId.SELECT_ALL_SUCCESSORS,
    name: StringId.SELECT_ALL_SUCCESSORS_COMMAND,
    keyTriggers: { base: 'Shift+]' }
  },
  {
    id: CommandId.SELECT_PREDECESSORS,
    name: StringId.SELECT_PREDECESSORS_COMMAND,
    keyTriggers: { base: '[' }
  },
  {
    id: CommandId.SELECT_ALL_PREDECESSORS,
    name: StringId.SELECT_ALL_PREDECESSORS_COMMAND,
    keyTriggers: { base: 'Shift+[' }
  },
  {
    id: CommandId.SELECT_NEIGHBORS,
    name: StringId.SELECT_NEIGHBORS_COMMAND,
    keyTriggers: { base: '\\' }
  },
  {
    id: CommandId.SELECT_ALL_NEIGHBORS,
    name: StringId.SELECT_ALL_NEIGHBORS_COMMAND,
    keyTriggers: { base: 'Shift+\\' }
  },

  {
    id: CommandId.TOGGLE_COLOR_MODE,
    name: StringId.TOGGLE_COLOR_MODE_COMMAND,
    keyTriggers: { base: '`' }
  },

  {
    id: CommandId.TOOL_ARRANGE,
    name: StringId.ARRANGE_TOOL_NAME,
    shortName: StringId.ARRANGE_TOOL_SHORT_NAME,
    keyTriggers: { base: 'A' },
    icon: APP_ICON_UP_DOWN_LEFT_RIGHT,
    description: StringId.ARRANGE_TOOL_DESCRIPTION
  },
  {
    id: CommandId.TOOL_CREATE,
    name: StringId.CREATE_TOOL_NAME,
    shortName: StringId.CREATE_TOOL_SHORT_NAME,
    keyTriggers: { base: 'C' },
    icon: APP_ICON_ARROW_POINTER,
    description: StringId.CREATE_TOOL_DESCRIPTION
  },
  {
    id: CommandId.TOOL_PAN,
    name: StringId.PAN_TOOL_NAME,
    shortName: StringId.PAN_TOOL_SHORT_NAME,
    keyTriggers: { base: 'Space' },
    icon: APP_ICON_HAND,
    description: StringId.PAN_TOOL_DESCRIPTION
  },
  {
    id: CommandId.TOOL_SELECT,
    name: StringId.SELECT_TOOL_NAME,
    shortName: StringId.SELECT_TOOL_SHORT_NAME,
    keyTriggers: { base: 'S' },
    icon: APP_ICON_PLUS,
    description: StringId.SELECT_TOOL_DESCRIPTION
  },

  {
    id: CommandId.NEXT_LOCALE,
    name: StringId.NEXT_LOCALE_COMMAND,
    keyTriggers: { base: 'Control+`' }
  },
  {
    id: CommandId.PREV_LOCALE,
    name: StringId.PREV_LOCALE_COMMAND,
    keyTriggers: { base: 'Control+Shift+`' }
  },

  {
    id: CommandId.UNDO,
    name: StringId.UNDO_COMMAND,
    keyTriggers: { base: 'Control+Z', mac: 'Command+Z' },
    icon: APP_ICON_UNDO,
    description: StringId.UNDO_DESCRIPTION
  },
  {
    id: CommandId.REDO,
    name: StringId.REDO_COMMAND,
    keyTriggers: { base: 'Control+Y', mac: 'Command+Shift+Z' },
    icon: APP_ICON_REDO,
    description: StringId.REDO_DESCRIPTION
  },

  {
    id: CommandId.ZOOM_TO_FIT,
    name: StringId.ZOOM_TO_FIT_COMMAND,
    keyTriggers: { base: 'Z' }
  },
  {
    id: CommandId.ZOOM_100_PERCENT,
    name: StringId.ZOOM_TO_100_PERCENT_COMMAND,
    keyTriggers: { base: '1' }
  },
  {
    id: CommandId.ZOOM_200_PERCENT,
    name: StringId.ZOOM_TO_200_PERCENT_COMMAND,
    keyTriggers: { base: '2' }
  },
  {
    id: CommandId.CENTER_ON_POINT,
    name: StringId.CENTER_ON_POINT_COMMAND,
    keyTriggers: { base: 'E' }
  },
  {
    id: CommandId.FOCUS_SELECTION,
    name: StringId.FOCUS_SELECTION_COMMAND,
    keyTriggers: { base: 'F' }
  },
  {
    id: CommandId.TOGGLE_BOTTOM_BAR,
    name: 'Toggle Bottom Bar' as StringId,
    keyTriggers: { base: 'Control+0' }
  },
  {
    id: CommandId.SHOW_EDITOR,
    name: StringId.SHOW_EDITOR_COMMAND,
    keyTriggers: { base: 'Tab' }
  },
  {
    id: CommandId.LAYOUT_LEFT_TO_RIGHT,
    name: StringId.LAYOUT_LEFT_TO_RIGHT
  },
  {
    id: CommandId.LAYOUT_TOP_TO_BOTTOM,
    name: StringId.LAYOUT_TOP_TO_BOTTOM
  },
  {
    id: CommandId.LAYOUT_RIGHT_TO_LEFT,
    name: StringId.LAYOUT_RIGHT_TO_LEFT
  },
  {
    id: CommandId.LAYOUT_BOTTOM_TO_TOP,
    name: StringId.LAYOUT_BOTTOM_TO_TOP
  },
  {
    id: CommandId.LAYOUT_UNCONSTRAINED,
    name: StringId.LAYOUT_UNCONSTRAINED
  },
  {
    id: CommandId.TOGGLE_BUNDLED_ROUTING,
    name: StringId.TOGGLE_BUNDLED_ROUTING
  },
  {
    id: CommandId.TOGGLE_ALIGN_ROOTS,
    name: StringId.TOGGLE_ALIGN_ROOTS
  },
  {
    id: CommandId.TOGGLE_ALIGN_LEAVES,
    name: StringId.TOGGLE_ALIGN_LEAVES
  },
  {
    id: CommandId.COLLAPSE_GROUPS,
    name: StringId.COLLAPSE_GROUPS_COMMAND,
    keyTriggers: { base: 'Control+Up', mac: 'Command+Up' }
  },
  {
    id: CommandId.DEEP_COLLAPSE_GROUPS,
    name: StringId.DEEP_COLLAPSE_GROUPS_COMMAND,
    keyTriggers: { base: 'Control+Shift+Up', mac: 'Command+Shift+Up' }
  },
  {
    id: CommandId.EXPAND_GROUPS,
    name: StringId.EXPAND_GROUPS_COMMAND,
    keyTriggers: { base: 'Control+Down', mac: 'Command+Down' }
  },
  {
    id: CommandId.DEEP_EXPAND_GROUPS,
    name: StringId.DEEP_EXPAND_GROUPS_COMMAND,
    keyTriggers: { base: 'Control+Shift+Down', mac: 'Command+Shift+Down' }
  },
  {
    id: CommandId.SET_SELECTED_UNITS_CLASS,
    name: StringId.SET_SELECTED_UNITS_CLASS_COMMAND
  },
  {
    id: CommandId.SET_LOCALE,
    name: StringId.SET_LOCALE_COMMAND
  }
];
